import React, { useState, useContext, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { ClientGroupsContext, ClientsContext, DivisionConfigContext } from "@/App";
import { Layers, Wand2, Check, Loader2, AlertTriangle, Users2, Lock, X, ChevronUp, ChevronDown, Plus, ListChecks } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback registry — the live list comes from GET /api/divisions, but these
// defaults keep the UI coherent before the fetch resolves. Must mirror
// DIVISIONS in backend/server.py (key + label + color).
const FALLBACK_DIVISIONS = [
  { key: "metallic", label: "Metallic", color: "#0F6E56", in_pipeline: true },
  { key: "drilling", label: "Drilling", color: "#8E1F2F", in_pipeline: true },
  { key: "ecotile",  label: "Ecotile",  color: "#1D9E75", in_pipeline: false },
];

// Soft tint for a division pill background from its hex (20 = ~12% alpha).
const tint = (hex) => `${hex}22`;

const VOCAB_AREAS = [
  { key: "procurement",   label: "Procurement" },
  { key: "manufacturing", label: "Manufacturing" },
  { key: "qc",            label: "Quality Control" },
  { key: "dispatch",      label: "Dispatch" },
];

// §3A status-vocabulary editor. Works on a draft copy of one division's
// config; Save pushes the whole division config to PUT /division-config/:div
// (backend enforces: locked undeletable, exactly one terminal, in-use keys
// undeletable — errors surface as toasts with the reason).
function VocabularyEditor() {
  const { config, refreshDivisionConfig } = useContext(DivisionConfigContext) || {};
  const [open, setOpen] = useState(false);
  const [div, setDiv] = useState("metallic");
  const [draft, setDraft] = useState(null);   // working copy of config[div]
  const [savingCfg, setSavingCfg] = useState(false);

  const startEditing = useCallback((d) => {
    setDiv(d);
    setDraft(JSON.parse(JSON.stringify((config || {})[d] || {})));
  }, [config]);

  useEffect(() => { if (open && !draft) startEditing(div); }, [open, draft, div, startEditing]);

  const mutate = (area, fn) => setDraft(prev => {
    const next = JSON.parse(JSON.stringify(prev));
    next[area] = next[area] || {};
    next[area].statuses = fn(next[area].statuses || []);
    return next;
  });

  const setLabel = (area, idx, label) => mutate(area, sts => { sts[idx].label = label; return sts; });
  const move = (area, idx, dir) => mutate(area, sts => {
    const j = idx + dir;
    if (j < 0 || j >= sts.length) return sts;
    [sts[idx], sts[j]] = [sts[j], sts[idx]];
    return sts;
  });
  const removeAt = (area, idx) => mutate(area, sts => sts.filter((_, i) => i !== idx));
  const addStatus = (area, label) => {
    const l = (label || "").trim();
    if (!l) return;
    mutate(area, sts => {
      if (sts.some(s => s.key.toLowerCase() === l.toLowerCase())) { toast.error("A status with this name already exists"); return sts; }
      // Insert before the terminal status so the end-state stays last.
      const ti = sts.findIndex(s => s.terminal);
      const entry = { key: l, label: l };
      return ti === -1 ? [...sts, entry] : [...sts.slice(0, ti), entry, ...sts.slice(ti)];
    });
  };

  const save = async () => {
    setSavingCfg(true);
    try {
      await axios.put(`${API}/api/division-config/${div}`, { config: draft });
      await refreshDivisionConfig?.();
      toast.success(`${div === "metallic" ? "Metallic" : "Drilling"} statuses saved — live for everyone now`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save statuses");
    } finally { setSavingCfg(false); }
  };

  return (
    <div className="mt-6 pt-4 border-t border-[hsl(var(--border))]/60">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
        data-testid="vocab-editor-toggle"
      >
        <ListChecks className="w-3.5 h-3.5" />
        Status vocabularies
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="mt-3">
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3 leading-relaxed">
            The status names each division's stage dropdowns offer. Rename freely — the stored key never changes, so history and logic stay intact.
            Locked statuses anchor the pipeline (start / advance gate) and can't be removed; a status still used by items can't be removed either.
          </p>
          <div className="flex items-center gap-1.5 mb-3">
            {["metallic", "drilling"].map(d => (
              <button key={d} onClick={() => startEditing(d)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${div === d ? "" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}
                style={div === d ? { color: divisionColorLocal(d), background: tint(divisionColorLocal(d)) } : {}}
                data-testid={`vocab-div-${d}`}
              >
                {d === "metallic" ? "Metallic" : "Drilling"}
              </button>
            ))}
            <button
              onClick={save}
              disabled={savingCfg || !draft}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-50 transition-colors"
              style={{ background: "#f97316" }}
              data-testid="vocab-save"
            >
              {savingCfg ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save {div === "metallic" ? "Metallic" : "Drilling"}
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {VOCAB_AREAS.map(area => {
              const sts = draft?.[area.key]?.statuses || [];
              return (
                <div key={area.key} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-2">{area.label}</p>
                  <div className="space-y-1">
                    {sts.map((s, i) => (
                      <div key={s.key} className="flex items-center gap-1.5">
                        <input
                          value={s.label}
                          onChange={e => setLabel(area.key, i, e.target.value)}
                          className="flex-1 min-w-0 h-7 px-2 text-xs rounded border border-[hsl(var(--border))]/60 bg-[hsl(var(--muted))]/20 focus:outline-none focus:ring-1 focus:ring-orange-500/60"
                          data-testid={`vocab-${div}-${area.key}-${i}`}
                        />
                        {s.label !== s.key && (
                          <span className="text-[9px] font-mono-data text-[hsl(var(--muted-foreground))] shrink-0" title={`Stored as "${s.key}" — renames never break history`}>({s.key})</span>
                        )}
                        {s.terminal && (
                          <span className="text-[9px] font-bold text-emerald-600 shrink-0" title="Advance gate — the item must reach this status to move on">END</span>
                        )}
                        <button onClick={() => move(area.key, i, -1)} disabled={i === 0} className="p-0.5 text-[hsl(var(--muted-foreground))] disabled:opacity-25 hover:text-[hsl(var(--foreground))]"><ChevronUp className="w-3 h-3" /></button>
                        <button onClick={() => move(area.key, i, 1)} disabled={i === sts.length - 1} className="p-0.5 text-[hsl(var(--muted-foreground))] disabled:opacity-25 hover:text-[hsl(var(--foreground))]"><ChevronDown className="w-3 h-3" /></button>
                        {s.locked ? (
                          <span title="Anchor status — can't be removed"><Lock className="w-3 h-3 text-[hsl(var(--muted-foreground))]/60 shrink-0" /></span>
                        ) : (
                          <button onClick={() => removeAt(area.key, i)} className="p-0.5 text-[hsl(var(--muted-foreground))] hover:text-red-500" title="Remove (refused if any item still holds it)"><X className="w-3 h-3" /></button>
                        )}
                      </div>
                    ))}
                  </div>
                  <AddStatusInput onAdd={(label) => addStatus(area.key, label)} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function AddStatusInput({ onAdd }) {
  const [val, setVal] = useState("");
  return (
    <div className="flex items-center gap-1.5 mt-2">
      <input
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") { onAdd(val); setVal(""); } }}
        placeholder="Add a status…"
        className="flex-1 min-w-0 h-7 px-2 text-xs rounded border border-dashed border-[hsl(var(--border))] bg-transparent focus:outline-none focus:ring-1 focus:ring-orange-500/60 placeholder:text-[hsl(var(--muted-foreground))]/50"
      />
      <button onClick={() => { onAdd(val); setVal(""); }} className="p-1 rounded text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" title="Add">
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// Local colour lookup for the editor (falls back to the fallback registry).
function divisionColorLocal(key) {
  return (FALLBACK_DIVISIONS.find(d => d.key === key) || {}).color || "#f97316";
}

export default function DivisionsPanel() {
  const { clientGroups, fetchClientGroups } = useContext(ClientGroupsContext);
  const { clients, fetchClients } = useContext(ClientsContext);

  const [divisions, setDivisions] = useState(FALLBACK_DIVISIONS);
  const [savingClientId, setSavingClientId] = useState(null);
  const [bulkGroupId, setBulkGroupId] = useState(null);
  const [confirm, setConfirm] = useState(null); // {client, division} | {groupBulk}

  const fetchDivisions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/divisions`);
      if (Array.isArray(res.data) && res.data.length) setDivisions(res.data);
    } catch { /* fallback list is fine */ }
  }, []);

  useEffect(() => { fetchDivisions(); }, [fetchDivisions]);

  const divByKey = useMemo(() => {
    const m = {};
    divisions.forEach(d => { m[d.key] = d; });
    return m;
  }, [divisions]);

  // Build the client-group → clients structure. A client belongs to a group via
  // the group's client_ids array. Clients in no group fall into an "Ungrouped"
  // bucket so nothing is hidden.
  const topGroups = useMemo(
    () => clientGroups.filter(g => !g.parent_id),
    [clientGroups]
  );

  const clientsById = useMemo(() => {
    const m = {};
    clients.forEach(c => { m[c.id] = c; });
    return m;
  }, [clients]);

  const grouped = useMemo(() => {
    const assigned = new Set();
    const rows = topGroups.map(g => {
      // include the group's own clients + those of its subgroups
      const subIds = clientGroups
        .filter(s => s.parent_id === g.id)
        .flatMap(s => s.client_ids || []);
      const ids = [...new Set([...(g.client_ids || []), ...subIds])];
      const gc = ids.map(id => clientsById[id]).filter(Boolean);
      gc.forEach(c => assigned.add(c.id));
      return { group: g, clients: gc.sort((a, b) => a.name.localeCompare(b.name)) };
    }).filter(r => r.clients.length);
    const ungrouped = clients
      .filter(c => !assigned.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { rows, ungrouped };
  }, [topGroups, clientGroups, clients, clientsById]);

  const itemCountNote = (client, division) =>
    `Reassigning ${client.name} to ${divByKey[division]?.label || division} also moves its items and updates who can see them.`;

  // ── Actions ──────────────────────────────────────────────────────────────
  const applyClientDivision = useCallback(async (client, division) => {
    setSavingClientId(client.id);
    try {
      const res = await axios.put(`${API}/api/clients/${client.id}`, { division });
      const moved = res?.data?._cascaded_items ?? 0;
      await Promise.all([fetchClients(), fetchDivisions()]);
      toast.success(
        moved > 0
          ? `${client.name} → ${divByKey[division]?.label}. ${moved} item${moved === 1 ? "" : "s"} moved.`
          : `${client.name} → ${divByKey[division]?.label}.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change division");
    } finally {
      setSavingClientId(null);
      setConfirm(null);
    }
  }, [fetchClients, fetchDivisions, divByKey]);

  const applyGroupBulk = useCallback(async (group, division) => {
    setBulkGroupId(group.id);
    try {
      const res = await axios.post(`${API}/api/divisions/set-group`, {
        group_id: group.id, division,
      });
      const { clients_updated = 0, items_updated = 0 } = res.data || {};
      await Promise.all([fetchClients(), fetchDivisions()]);
      toast.success(
        `${group.name} → ${divByKey[division]?.label}. ${clients_updated} client${clients_updated === 1 ? "" : "s"}, ${items_updated} item${items_updated === 1 ? "" : "s"} moved.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set the group");
    } finally {
      setBulkGroupId(null);
      setConfirm(null);
    }
  }, [fetchClients, fetchDivisions, divByKey]);

  // Change handler that gates behind a confirm when items would move
  const requestClientChange = (client, division) => {
    if (division === (client.division || "metallic")) return; // no-op
    setConfirm({ type: "client", client, division });
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const DivisionPill = ({ dk }) => {
    const d = divByKey[dk] || { label: dk, color: "#888" };
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg"
        style={{ color: d.color, background: tint(d.color) }}
      >
        <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
        {d.label}
      </span>
    );
  };

  const ClientRow = ({ client, expectedDivision }) => {
    const cur = client.division || "metallic";
    const busy = savingClientId === client.id;
    // Mismatch hint (Phase 1b linkage): the client's division disagrees with
    // its group tree's division — visible, never silently fixed.
    const mismatch = expectedDivision && expectedDivision !== cur;
    return (
      <div className={`flex items-center gap-3 p-2.5 rounded-lg border bg-[hsl(var(--card))] ${mismatch ? "border-amber-500/50" : "border-[hsl(var(--border))]"}`}>
        <span className="w-4 h-4 rounded-full shrink-0" style={{ background: client.color || "#94a3b8" }} />
        <span className="text-sm flex-1 min-w-0 truncate">{client.name}</span>
        {mismatch && !busy && (
          <button
            type="button"
            onClick={() => setConfirm({ type: "client", client, division: expectedDivision })}
            className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 hover:underline whitespace-nowrap"
            title={`This client sits in a ${divByKey[expectedDivision]?.label} group but is tagged ${divByKey[cur]?.label}. Click to fix (items move after confirm).`}
            data-testid={`division-mismatch-${client.id}`}
          >
            ⚠ in {divByKey[expectedDivision]?.label} group — fix
          </button>
        )}
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin text-[hsl(var(--muted-foreground))]" />
        ) : (
          <div className="flex items-center gap-2">
            <DivisionPill dk={cur} />
            <select
              value={cur}
              onChange={(e) => requestClientChange(client, e.target.value)}
              className="text-xs h-8 px-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 focus:outline-none focus:ring-1 focus:ring-orange-500 cursor-pointer"
              aria-label={`Division for ${client.name}`}
            >
              {divisions.map(d => (
                <option key={d.key} value={d.key}>{d.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    );
  };

  const setGroupDivisionAttr = useCallback(async (group, division) => {
    try {
      await axios.put(`${API}/api/client-groups/${group.id}`, { division });
      await fetchClientGroups();
      toast.success(division
        ? `"${group.name}" is now the ${divByKey[division]?.label} division — new clients added to it will inherit this`
        : `"${group.name}" no longer carries a division`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set the group's division");
    }
  }, [fetchClientGroups, divByKey]);

  const GroupBlock = ({ group, list }) => (
    <div className="mb-5">
      <div className="flex items-center gap-2.5 mb-2 flex-wrap">
        <span
          className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[11px] font-bold shrink-0"
          style={{ background: group.color || "#6366f1" }}
        >
          {group.name.slice(0, 2).toUpperCase()}
        </span>
        <span
          className="text-sm font-semibold px-2 py-0.5 rounded"
          style={{ color: group.color, background: (group.color || "#6366f1") + "18" }}
        >
          {group.name}
        </span>
        {/* Linkage (Phase 1b): the group's division attribute — new clients
            created into this group tree inherit it automatically. */}
        <select
          value={group.division || ""}
          onChange={(e) => setGroupDivisionAttr(group, e.target.value)}
          className="text-[11px] h-7 px-1.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 focus:outline-none focus:ring-1 focus:ring-orange-500 cursor-pointer"
          title="Division this group belongs to — new clients added here inherit it"
          data-testid={`group-division-${group.id}`}
        >
          <option value="">no division</option>
          {divisions.map(d => <option key={d.key} value={d.key}>{d.label} division</option>)}
        </select>
        <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{list.length} clients</span>
        {/* Set-all shortcut */}
        <div className="ml-auto flex items-center gap-1.5">
          {bulkGroupId === group.id ? (
            <Loader2 className="w-4 h-4 animate-spin text-[hsl(var(--muted-foreground))]" />
          ) : (
            <>
              <span className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-1">
                <Wand2 className="w-3 h-3" /> set all to
              </span>
              {divisions.map(d => (
                <button
                  key={d.key}
                  onClick={() => setConfirm({ type: "group", group, division: d.key, list })}
                  className="text-[11px] font-semibold px-2 py-1 rounded-md transition-colors hover:brightness-95"
                  style={{ color: d.color, background: tint(d.color) }}
                >
                  {d.label}
                </button>
              ))}
            </>
          )}
        </div>
      </div>
      <div className="space-y-1.5">
        {list.map(c => <ClientRow key={c.id} client={c} expectedDivision={group.division || null} />)}
      </div>
    </div>
  );

  return (
    <div className="mt-4">
      {/* Header */}
      <div className="mb-3">
        <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] flex items-center gap-1.5">
          <Layers className="w-3.5 h-3.5" /> Client to division mapping
        </p>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 leading-relaxed">
          Every client belongs to one division. This drives who sees it and where its work is counted.
          Changing a client's division moves its items too. Use "set all to" to map a whole group at once.
        </p>
      </div>

      {/* Division legend */}
      <div className="flex items-center gap-2 flex-wrap mb-4 pb-3 border-b border-[hsl(var(--border))]/60">
        {divisions.map(d => (
          <span key={d.key} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg"
                style={{ color: d.color, background: tint(d.color) }}>
            <span className="w-2 h-2 rounded-full" style={{ background: d.color }} />
            {d.label}
            <span className="opacity-70">· {d.client_count ?? 0}</span>
            {!d.in_pipeline && <span className="opacity-60 text-[10px]">(own module)</span>}
          </span>
        ))}
      </div>

      {/* Grouped mapping */}
      {grouped.rows.map(r => (
        <GroupBlock key={r.group.id} group={r.group} list={r.clients} />
      ))}

      {grouped.ungrouped.length > 0 && (
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-6 h-6 rounded-full flex items-center justify-center bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] shrink-0">
              <Users2 className="w-3.5 h-3.5" />
            </span>
            <span className="text-sm font-semibold text-[hsl(var(--muted-foreground))]">Not in a group</span>
            <span className="text-[11px] text-[hsl(var(--muted-foreground))]">{grouped.ungrouped.length} clients</span>
          </div>
          <div className="space-y-1.5">
            {grouped.ungrouped.map(c => <ClientRow key={c.id} client={c} />)}
          </div>
        </div>
      )}

      {clients.length === 0 && (
        <div className="text-center py-10 text-sm text-[hsl(var(--muted-foreground))]">
          No clients yet. Add clients in the Clients tab, then map them to divisions here.
        </div>
      )}

      {/* ── Status vocabularies (Phase 1b, §3A editor) ─────────────────────
          Rules: labels editable; new statuses addable/reorderable; LOCKED
          anchor statuses can't be removed (lock shown); a status still held
          by items can't be removed (backend refuses with the live count);
          keys are immutable — a "rename" changes only the label. */}
      <VocabularyEditor />

      {/* Confirm dialog (cascade warning) */}
      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
             onClick={() => setConfirm(null)}>
          <div className="w-full max-w-md rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-xl p-5"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                   style={{ background: "rgba(245,158,11,0.14)" }}>
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-[hsl(var(--foreground))]">Confirm before it applies</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1 leading-relaxed">
                  {confirm.type === "client"
                    ? itemCountNote(confirm.client, confirm.division)
                    : `Setting every client in "${confirm.group.name}" (${confirm.list.length}) to ${divByKey[confirm.division]?.label} will reassign each client and move all their items. Nothing changes until you confirm.`}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setConfirm(null)}
                      className="text-xs px-3 py-2 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors">
                Cancel
              </button>
              <button
                onClick={() => confirm.type === "client"
                  ? applyClientDivision(confirm.client, confirm.division)
                  : applyGroupBulk(confirm.group, confirm.division)}
                className="text-xs px-3 py-2 rounded-lg font-semibold text-white flex items-center gap-1.5 transition-colors"
                style={{ background: "#f97316" }}>
                <Check className="w-3.5 h-3.5" /> Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
